import { useEffect, useMemo, useState } from "react";
import NpiDashboardProjectList from "./npi-dashboard-project-list.component";
import { BaseService } from "../../../../../services/base-service";
import { URLS } from "../../../../../services/app-urls";
import { Box, Button, InputAdornment, Menu, MenuItem, Paper, TextField } from "@mui/material";
import { FileDownloadOutlined, Search } from "@mui/icons-material";
import { translate } from "../../../../../translate/translate";
import NpiDashboardSummaryComponent from "./npi-dashboard-summary.component";
import NpiDashboardLegendComponent from "./npi-dashboard-legend.component";
import { exportNpiDashboardImage, NPI_DASHBOARD_CAPTURE_ID } from "../utils/export-dashboard-image";
import { toast } from "react-toastify";
import { outlinedFieldHover } from "../styles/npi-dashboard.styles";
import { NpiDashboardData } from "../types/npi-project.types";
import {mapNpiDetailToProject} from "../utils/map-npi-detail";

interface NpiDashboardBoardProps {
    clientId: number | null;
    year: number | null;
    projectTypeId: number | null;
    refreshKey: number;
    clientName: string;
    projectTypeName: string;
}

const NpiDashboardBoardComponent = ({
    clientId,
    year,
    projectTypeId,
    refreshKey,
    clientName,
    projectTypeName
}: NpiDashboardBoardProps) => {
    const [dashboardData, setDashboardData] = useState<NpiDashboardData | null>(null);
    const [search, setSearch] = useState<string>("");
    const [debouncedSearch, setDebouncedSearch] = useState<string>("");
    const [exportAnchor, setExportAnchor] = useState<HTMLElement | null>(null);
    const [exporting, setExporting] = useState<boolean>(false);
    const [reloadKey, setReloadKey] = useState<number>(0);

    useEffect(() => {
        const debounceTimeout = 300;
        const timeoutId = window.setTimeout(() => setDebouncedSearch(search.trim()), debounceTimeout);
        return () => window.clearTimeout(timeoutId);
    }, [search]);

    useEffect(() => {
        if (!projectTypeId) {
            setDashboardData(null);
            return;
        }

        let isCurrentRequest = true;
        const npiService = new BaseService<any>(URLS.NPI_DASHBOARD_DATA);
        npiService.clearParameters();
        npiService.addParameter("project_type", projectTypeId);
        if (clientId) {
            npiService.addParameter("client", clientId);
        }
        if (year) {
            npiService.addParameter("year", year);
        }
        if (debouncedSearch) {
            npiService.addParameter("search", debouncedSearch);
        }

        npiService.get().then((response) => {
            if (!isCurrentRequest) {
                return;
            }
            const data = response.data;
            data.projects = data.projects.map((project: any) => mapNpiDetailToProject(project));
            setDashboardData(data);
        });

        return () => {
            isCurrentRequest = false;
        };
    }, [clientId, year, projectTypeId, refreshKey, debouncedSearch, reloadKey]);

    const handleExportImage = async () => {
        setExportAnchor(null);
        setExporting(true);
        try {
            await exportNpiDashboardImage({ clientName, year: year ?? "", projectTypeName });
        } catch {
            toast.error(translate.t("export_image_error"));
        } finally {
            setExporting(false);
        }
    };

    return (
      <Box sx={{ pr: "15px", pt: 2, boxSizing: "border-box" }}>
          <Box id={NPI_DASHBOARD_CAPTURE_ID}>
              <NpiDashboardSummaryComponent
                steps_pending={dashboardData?.steps_pending || 0}
                steps_in_progress={dashboardData?.steps_in_progress || 0}
                steps_finished={dashboardData?.steps_finished || 0}
                total_projects={dashboardData?.total_projects || 0}
                total_steps={dashboardData?.total_steps || 0}
              />
                  <Paper variant="outlined" sx={{ borderRadius: "16px", width: "auto" }} id={"dashboard-container-export"}>
                      <Box
                          data-export-ignore
                          sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              flexWrap: "wrap",
                              gap: 2,
                              borderBottom: "1px solid #E2E8F0",
                              px: 3,
                              py: 2,
                              width: "auto",
                              boxSizing: "border-box",
                            }}>
                          <TextField
                              size="small"
                              value={search}
                              onChange={(event) => setSearch(event.target.value)}
                              placeholder={translate.t("search_step_or_project")}
                              sx={{
                                  width: 400,
                                  maxWidth: "100%",
                                  backgroundColor: "#fff",
                                  ...outlinedFieldHover("8px"),
                                }}
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                              <Search fontSize="small" />
                                          </InputAdornment>
                                      ),
                                    },
                                }}
                                />
                          <Button
                              variant="outlined"
                              startIcon={<FileDownloadOutlined />}
                              disabled={exporting || !projectTypeId}
                              onClick={(event) => setExportAnchor(event.currentTarget)}
                              sx={{ color: "#003FFB", borderColor: "#003FFB", fontWeight: 700, "&:hover": { borderColor: "#003FFB", backgroundColor: "#F5F8FF" } }}>
                              {translate.t("export_label").toUpperCase()}
                          </Button>
                          <Menu anchorEl={exportAnchor} open={Boolean(exportAnchor)} onClose={() => setExportAnchor(null)}>
                              <MenuItem onClick={handleExportImage}>{translate.t("export_image_png")}</MenuItem>
                          </Menu>
                      </Box>
                      <Box sx={{ display: "flex", justifyContent: "flex-end", px: 3, pt: 2, width: "auto", marginBottom: 1 }}>
                          <NpiDashboardLegendComponent />
                      </Box>
                      <NpiDashboardProjectList projects={dashboardData?.projects || []} term={debouncedSearch} onProjectDeleted={() => setReloadKey((current) => current + 1)}/>
                  </Paper>
          </Box>
      </Box>
    );
};

export default NpiDashboardBoardComponent;
